import React, { Component, Fragment, useState, memo } from "react";
import { hot } from "react-hot-loader";
import { withStyles, createStyles } from "@material-ui/core/styles";
import {
  SortableContainer,
  SortableElement,
  arrayMove,
} from "react-sortable-hoc";
import { map } from "lodash";
import Form from "../../../Form";
import {
  Avatar,
  ExpansionPanel as MuiExpansionPanel,
  ExpansionPanelSummary as MuiExpansionPanelSummary,
  ExpansionPanelDetails as MuiExpansionPanelDetails,
  Typography,
  FormLabel,
  Menu,
  MenuItem,
  IconButton,
  Grid,
  Button,
  Box,
} from "@material-ui/core";
import MoreVertIcon from "@material-ui/icons/MoreVert";
import DeleteIcon from "@material-ui/icons/Delete";
import DragHandleIcon from "@material-ui/icons/DragIndicator";
import { renderValues } from "../../../../utils/functions";

const ExpansionPanel = withStyles({
  root: {
    position: "static",
    zIndex: 4,
    border: "1px solid rgba(0, 0, 0, .125)",
    boxShadow: "none",
    "&:not(:last-child)": {
      borderBottom: 0,
    },
    "&:before": {
      display: "none",
    },
    "&$expanded": {
      margin: "auto",
    },
  },
  expanded: {},
})(MuiExpansionPanel);

const ExpansionPanelSummary = withStyles((theme) => ({
  root: {
    position: "relative",
    padding: theme.spacing(0.5, 1),
    backgroundColor: "rgba(0, 0, 0, .03)",
    borderBottom: "1px solid rgba(0, 0, 0, .125)",
    marginBottom: -1,
    minHeight: 0,
    "&$expanded": {
      minHeight: 0,
    },
  },
  content: {
    margin: 0,
    position: "relative",
    "&$expanded": {
      margin: 0,
    },
  },
  expanded: {},
}))(MuiExpansionPanelSummary);

const ExpansionPanelDetails = withStyles((theme) => ({
  root: {
    padding: theme.spacing(2),
  },
}))(MuiExpansionPanelDetails);

const styles = createStyles((theme) => ({
  actions: {
    "& div": {
      margin: theme.spacing(1.5),
    },
    textAlign: "right",
  },
  avatar: {
    display: "inline-flex",
    height: "1.75rem",
    width: "1.75rem",
    fontSize: "1rem",
    marginRight: theme.spacing(1),
  },
  addBtn: {
    margin: theme.spacing(1, 0),
    border: `1px dashed ${theme.palette.grey["400"]}`,
  },
}));

const ContextMenu = memo(
  ({ items }) => {
    const [anchorEl, setAnchorEl] = useState(null);
    const handleContextMenu = (e) => {
      e.stopPropagation();
      setAnchorEl(e.currentTarget);
    };

    const handleClose = () => setAnchorEl(null);

    return (
      <Fragment>
        <IconButton
          aria-label="More"
          aria-controls="long-menu"
          aria-owns="long-menu"
          aria-haspopup="true"
          onClick={handleContextMenu}
        >
          <MoreVertIcon />
        </IconButton>
        <Menu
          keepMounted
          id="long-menu"
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleClose}
        >
          {map(items, (item) => (
            <MenuItem
              key={item.label}
              onClick={(e) => {
                handleClose();
                item.onClick(e);
              }}
            >
              {item.label}
            </MenuItem>
          ))}
        </Menu>
      </Fragment>
    );
  },
  () => true
);

class Repeater extends Component {
  state = {
    updated: Date.now(),
    expanded: false,
    rows: this.props.value || [],
  };

  static getDerivedStateFromProps(props, state) {
    return {
      ...state,
      rows: props.value || [],
    };
  }

  handleChange = (panel) => (event, newExpanded) => {
    this.setState({ expanded: newExpanded ? panel : false });
  };

  addRow = (index) => (e) => {
    e.stopPropagation();
    const { rows, expanded } = this.state;
    let newRows;
    if (index !== undefined) {
      index = index < 0 ? 0 : index;
      newRows = [...rows.slice(0, index), {}, ...rows.slice(index)];
      if (expanded !== false) {
        this.setState({ expanded: index });
      }
    } else {
      newRows = [...rows, {}];
      if (expanded !== false) {
        this.setState({ expanded: newRows.length - 1 });
      }
    }

    this.props.updateForm(this.props.keyPrefix)(newRows);

    if (!rows.length) {
      this.setState({ expanded: newRows.length - 1 });
    }
  };

  removeRow = (index) => (e) => {
    const { rows, expanded } = this.state;
    e.stopPropagation();
    const newRows = [...rows.slice(0, index), ...rows.slice(index + 1)];
    if (index === expanded) {
      this.setState({ expanded: false });
    }
    this.props.updateForm(this.props.keyPrefix)(newRows);
  };

  onSortEnd = ({ oldIndex, newIndex }) => {
    const { rows, expanded } = this.state;
    const newRowOrder = arrayMove(rows, oldIndex, newIndex);
    // this.setState({ rows: arrayMove(rows, oldIndex, newIndex) });

    if (oldIndex === expanded) {
      this.setState({ expanded: newIndex });
    }

    this.props.updateForm(this.props.keyPrefix)(newRowOrder);
    this.setState({ updated: Date.now() });
  };

  SortableItem = SortableElement(({ row, rowIndex: index = 0 }) => {
    // const isExpanded = this.state.expanded === index;
    return (
      <ExpansionPanel
        square
        expanded={this.state.expanded === index}
        onChange={this.handleChange(index)}
      >
        <ExpansionPanelSummary
          aria-controls="panel1d-content"
          id="panel1d-header"
        >
          <Grid container alignItems="center">
            <Grid item xs={10}>
              <span style={{ marginRight: "0.5rem", cursor: "move" }}>
                <DragHandleIcon style={{ verticalAlign: "middle" }} />
              </span>
              <Avatar className={this.props.classes.avatar}>{index + 1}</Avatar>
              <Typography component="span">
                {renderValues({
                  value: row,
                  properties: this.props.properties,
                  joinWith: " / ",
                  maxLength: 80,
                })}
              </Typography>
            </Grid>
            <Grid item xs={2}>
              <div className={this.props.classes.actions}>
                <IconButton onClick={this.removeRow(index)}>
                  <DeleteIcon />
                </IconButton>
                <ContextMenu
                  items={[
                    { label: "Remove Row", onClick: this.removeRow(index) },
                    { label: "Add Row Before", onClick: this.addRow(index) },
                    { label: "Add Row After", onClick: this.addRow(index + 1) },
                  ]}
                />
              </div>
            </Grid>
          </Grid>
        </ExpansionPanelSummary>
        <ExpansionPanelDetails>
          {
            <Grid
              container
              style={{
                display: this.state.expanded === index ? "flex" : "none",
              }}
              spacing={2}
            >
              <Form
                key={`${this.props.keyPrefix}[${index}]`}
                handleInputChange={this.props.updateForm}
                keyPrefix={`${this.props.keyPrefix}[${index}]`}
                schema={this.props}
                formData={this.props.formData}
              />
            </Grid>
          }
        </ExpansionPanelDetails>
      </ExpansionPanel>
    );
  });

  SortableList = SortableContainer(({ rows }) => {
    // const unique = Date.now();
    const { SortableItem } = this;
    return (
      <div>
        {map(rows, (row, index) => (
          <SortableItem
            key={`item-${index}-${this.state.updated}`}
            index={index}
            rowIndex={index}
            row={row}
          />
        ))}
      </div>
    );
  });

  render() {
    return (
      <div>
        <Grid container alignItems="center">
          <Grid item xs={12}>
            <FormLabel>{this.props.label}</FormLabel>
          </Grid>
        </Grid>
        {/* {Boolean(rows.length) && (
        <Button
          onClick={addRow(rows.length)}
          fullWidth
          className={classes.addBtn}input:-webkit-autofill,
        >
          Add {props.label}
        </Button>
      )} */}

        <this.SortableList
          onSortEnd={this.onSortEnd}
          pressDelay={200}
          pressThreshold={5}
          rows={this.state.rows}
        />

        <Button
          onClick={this.addRow()}
          fullWidth
          className={this.props.classes.addBtn}
        >
          Add {this.props.label}
        </Button>
      </div>
    );
  }
}

export default hot(module)(withStyles(styles)(Repeater));
